import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getWatchlistItems, stockSearch } from "../../../api/user";
import AssetAvatar from "../../../components/Watchlist/AssetAvatar";
import { cleanSymbol, instrumentName, shortName } from "../../../components/Watchlist/watchlistFormat";
import { InfoCircleIcon, SearchIcon } from "./icons";

// How many suggestions a page of the browse list holds. Each row is
// price-enriched server-side, so pages stay small and "Load more" fetches the
// next 3 — the same size every other Add-Assets surface uses (AddAssetsPanel,
// MobileAddAssets). Keep it at 3 so the league flow loads like the rest of them.
const BROWSE_LIMIT = 3;

const CATEGORY_LABEL = {
  stocks: "Stocks",
  crypto: "Crypto",
  forex: "Forex",
};

// Map UI keys → the values stockSearch expects.
const API_CATEGORY = {
  stocks: "Stocks",
  crypto: "Crypto",
  forex: "Forex",
};

// Copy that follows the chosen category, so the section reads naturally whichever
// market the league trades: "Allowed Stocks … Every stock on QUSHi is fair game."
const CATEGORY_COPY = {
  stocks: { plural: "Stocks", lower: "stocks", one: "stock" },
  crypto: { plural: "Crypto", lower: "crypto", one: "coin" },
  forex: { plural: "Forex", lower: "forex", one: "pair" },
  mixed: { plural: "Assets", lower: "assets", one: "asset" },
};

const copyFor = (key) => CATEGORY_COPY[key] || CATEGORY_COPY.mixed;

// A row from either source, normalised to what a tile draws. Curated rows carry
// QUSHi's own brand art; provider search hits have none, so they fall back to the
// initials chip — the same split the watchlist Add-Assets panel makes, and the
// reason both live behind AssetAvatar.
const displayFor = (item, categoryKey) => {
  if (item.ticker) {
    // Search-result shape.
    return {
      chip: cleanSymbol(item.ticker, categoryKey),
      logo: null,
      name: instrumentName({ name: item.name, symbol: item.ticker, category: categoryKey }),
      symbol: item.ticker,
    };
  }
  const chip = cleanSymbol(item.symbol, categoryKey);
  return {
    chip,
    logo: item.logo || null,
    name: item.details?.stock_name
      ? shortName(item.details.stock_name)
      : item.watchlist_name || chip,
    symbol: item.details?.stock_symbol || item.symbol,
  };
};

// One suggestion: brand art (or initials), name, ticker. Selected takes the green
// treatment the rest of the flow uses for a made choice.
const AssetTile = ({ row, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`flex items-center gap-2.5 rounded-xl border bg-white px-2.5 py-2 text-left transition-colors ${
      selected ? "border-[#22A36B] bg-[#F0FBF5]" : "border-[#E5E7EB] hover:border-[#0F4146]"
    }`}
  >
    <AssetAvatar logo={row.logo} chip={row.chip} className="h-7 w-7" chipTextClass="text-[9px] font-semibold" />
    <span className="min-w-0">
      <span className="block text-xs font-semibold text-[#0F172A] truncate">{row.name}</span>
      <span className="block text-[10px] text-tertiary truncate">{row.chip}</span>
    </span>
  </button>
);

// Which assets are in play for a league.
//
// Two modes, per the design: "All <category>" puts the whole market in play and
// keeps the ticker list empty — that's what the API reads as "no restriction" —
// or "Custom List" limits the league to the tickers picked here. Switching to All
// leaves the picked tickers alone so a user who taps back and forth doesn't lose
// their list; the wizard is the one that drops them from the payload.
const AssetSelector = ({
  category, // "stocks" | "crypto" | "forex" | "mixed"
  mode = "all", // "all" | "custom"
  onModeChange, // (next: "all" | "custom") => void
  value, // array of ticker strings (with prefixes for crypto/forex)
  onChange, // (next: string[]) => void
  error,
}) => {
  // For Mixed leagues the user picks which sub-market to search in.
  const isMixed = category === "mixed";
  const [activeSub, setActiveSub] = useState(isMixed ? "stocks" : category);
  const activeKey = isMixed ? activeSub : category;

  useEffect(() => {
    if (!isMixed) setActiveSub(category);
  }, [category, isMixed]);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await stockSearch(
          { category: API_CATEGORY[activeKey], query: trimmed },
          controller.signal
        );
        if (!controller.signal.aborted) {
          setResults(res?.data || []);
        }
      } catch (e) {
        if (e?.name !== "CanceledError") setResults([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [query, activeKey]);

  // Browse list — QUSHi's curated category list, the same source (and the same
  // brand art) as the watchlist Add-Assets panel. Shown until the user types.
  const [browse, setBrowse] = useState([]);
  const [browsePage, setBrowsePage] = useState(1);
  const [browseHasMore, setBrowseHasMore] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);
  const browseReqRef = useRef(0);

  const loadBrowse = useCallback(async (key, page) => {
    const reqId = ++browseReqRef.current;
    setBrowseLoading(true);
    try {
      const res = await getWatchlistItems(key, page, BROWSE_LIMIT);
      // A stale page must never overwrite the current market's list.
      if (reqId !== browseReqRef.current) return;
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      setBrowse((prev) => (page === 1 ? items : [...prev, ...items]));
      setBrowsePage(page);
      setBrowseHasMore(res?.data?.pagination?.has_more ?? items.length === BROWSE_LIMIT);
    } catch (e) {
      if (reqId === browseReqRef.current) {
        console.error("Failed to load league assets:", e);
        if (page === 1) setBrowse([]);
      }
    } finally {
      if (reqId === browseReqRef.current) setBrowseLoading(false);
    }
  }, []);

  useEffect(() => {
    setBrowse([]);
    setBrowsePage(1);
    setBrowseHasMore(false);
    loadBrowse(activeKey, 1);
  }, [activeKey, loadBrowse]);

  const selected = value || [];
  // Matched on the BASE symbol as well as the raw string: the curated list and
  // the provider search can name the same asset differently ("BTC" vs
  // "X:BTCUSD"), and a tile the user already picked has to look picked either way.
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const selectedBases = useMemo(
    () => new Set(selected.map((t) => cleanSymbol(t))),
    [selected]
  );

  const toggle = (ticker) => {
    if (!ticker) return;
    if (selectedSet.has(ticker)) {
      onChange?.(selected.filter((t) => t !== ticker));
      return;
    }
    const base = cleanSymbol(ticker);
    if (selectedBases.has(base)) {
      onChange?.(selected.filter((t) => cleanSymbol(t) !== base));
      return;
    }
    onChange?.([...selected, ticker]);
  };

  const remove = (ticker) => onChange?.(selected.filter((t) => t !== ticker));

  const isSearchMode = !!query.trim();
  const suggestionItems = isSearchMode ? results : browse;
  const suggestionsLoading = isSearchMode ? loading : browseLoading;

  const isAll = mode === "all";
  // The heading follows the league's category (Mixed reads "Assets"); the search
  // copy follows whichever sub-market is open.
  const head = copyFor(isMixed ? "mixed" : category);
  const sub = copyFor(activeKey);

  return (
    <div>
      <div className="flex items-end justify-between gap-3 mb-2">
        <div>
          <span className="block font-semibold text-sm text-[#0F172A]">
            Allowed {head.plural}
          </span>
          <span className="block mt-0.5 text-xs text-tertiary">Pick the assets in play.</span>
        </div>
        <span className="shrink-0 text-xs font-medium text-[#22A36B]">
          {isAll ? `All ${head.lower} selected` : `${selected.length} selected`}
        </span>
      </div>

      {/* All vs Custom. Two halves of one control, so it reads as a single
          either/or rather than a pair of buttons. */}
      <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#F3F4F6] p-1">
        {[
          { key: "all", label: `All ${head.plural}` },
          { key: "custom", label: "Custom List" },
        ].map(({ key, label }) => (
          <button
            type="button"
            key={key}
            onClick={() => onModeChange?.(key)}
            aria-pressed={mode === key}
            // The unselected half keeps a transparent border so switching
            // doesn't shift the control by a pixel.
            className={`rounded-lg border py-2 text-sm font-medium transition-colors ${
              mode === key
                ? "bg-white text-[#0F4146] border-[#22A36B]"
                : "border-transparent text-[#66748C] hover:text-[#0F172A]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isAll ? (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3.5 py-3">
          <InfoCircleIcon className="w-4 h-4 mt-0.5 shrink-0" />
          <p className="text-sm text-[#0F172A]">
            Every {head.one} on QUSHi is fair game.
          </p>
        </div>
      ) : (
        <div className="mt-3">
          {isMixed && (
            <div className="mb-2 inline-flex rounded-full bg-[#F3F4F6] p-1">
              {["stocks", "crypto", "forex"].map((k) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => {
                    setActiveSub(k);
                    setQuery("");
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeSub === k ? "bg-white text-[#0F4146] shadow-sm" : "text-[#66748C]"
                  }`}
                >
                  {CATEGORY_LABEL[k]}
                </button>
              ))}
            </div>
          )}

          <div className="relative">
            <SearchIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${sub.lower}…`}
              className={`w-full rounded-full border pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#0F4146] ${
                error ? "border-red-400" : "border-[#E5E7EB]"
              }`}
            />
          </div>

          {/* What's in play, as removable chips — the list the league is actually
              limited to, above the suggestions it was built from. */}
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {selected.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3 py-1 text-xs font-medium text-[#0F172A]"
                >
                  {cleanSymbol(t)}
                  <button
                    type="button"
                    onClick={() => remove(t)}
                    aria-label={`Remove ${cleanSymbol(t)}`}
                    className="text-[#66748C] hover:text-red-500 text-sm leading-none"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {suggestionsLoading && suggestionItems.length === 0 ? (
            <p className="mt-3 text-xs text-secondary">
              {isSearchMode ? "Searching…" : "Loading assets…"}
            </p>
          ) : suggestionItems.length === 0 ? (
            <p className="mt-3 text-xs text-secondary">
              {isSearchMode
                ? "No matches. Try a different name or ticker."
                : "No assets to show."}
            </p>
          ) : (
            // Three across on desktop, so each loaded page lands as one full row
            // instead of a ragged half-row.
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
              {suggestionItems.map((item, i) => {
                const row = displayFor(item, activeKey);
                if (!row.symbol) return null;
                return (
                  <AssetTile
                    key={`${row.symbol}-${i}`}
                    row={row}
                    selected={
                      selectedSet.has(row.symbol) || selectedBases.has(cleanSymbol(row.symbol))
                    }
                    onClick={() => toggle(row.symbol)}
                  />
                );
              })}
            </div>
          )}

          {/* Browse only — a search returns everything it has in one go. */}
          {!isSearchMode && browseHasMore && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={() => loadBrowse(activeKey, browsePage + 1)}
                disabled={browseLoading}
                className="text-sm font-medium text-[#0F4146] hover:underline disabled:opacity-50"
              >
                {browseLoading ? "Loading…" : "Load more"}
              </button>
            </div>
          )}

          {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default AssetSelector;
